import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReleaseTrackSummary from './ReleaseTrackSummary';
import { ageChipSx, daysOldFromDate, getVersionMetaFromAssetMap } from './utils';

const getAvailability = (versionMeta) => {
  if (!versionMeta) return { hasCsv: false, hasParquet: false, hasSnowflake: false };
  const s3Locations = Array.isArray(versionMeta.s3_locations) ? versionMeta.s3_locations : [];
  return {
    hasCsv: s3Locations.some((loc) => loc?.format === 'csv'),
    hasParquet: s3Locations.some((loc) => loc?.format === 'parquet'),
    hasSnowflake: Boolean(versionMeta.snowflake_table),
  };
};

const getVersionTimestamp = (v) => {
  return v?.timestamp_utc || v?.version_timestamp_utc || null;
};

const addMinutesIso = (minutes) => {
  const dt = new Date(Date.now() + minutes * 60 * 1000);
  dt.setSeconds(0, 0);
  return dt.toISOString();
};

const toUtcInputParts = (value) => {
  const dt = value ? new Date(value) : new Date(addMinutesIso(5));
  if (Number.isNaN(dt.getTime())) return { date: '', time: '00:00' };

  return {
    date: dt.toISOString().slice(0, 10),
    time: dt.toISOString().slice(11, 16),
  };
};

const buildUtcIso = (date, time) => {
  if (!date || !time) return null;
  const dt = new Date(`${date}T${time}:00Z`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
};

const isPastOrTooSoon = (value) => {
  const dt = value ? new Date(value) : null;
  if (!dt || Number.isNaN(dt.getTime())) return true;
  return dt.getTime() <= Date.now();
};

const formatLocalFromUtc = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;

  return {
    full: dt.toLocaleString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const sortVersionsNewestFirst = (versions) => {
  return [...versions].sort((a, b) => {
    const aTs = getVersionTimestamp(a);
    const bTs = getVersionTimestamp(b);

    const aTime = aTs ? new Date(aTs).getTime() : -Infinity;
    const bTime = bTs ? new Date(bTs).getTime() : -Infinity;

    if (aTime !== bTime) return bTime - aTime;

    return String(b?.version || '').localeCompare(String(a?.version || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

const formatVersionOptionLabel = (versionObj) => {
  const version = versionObj?.version || '-';
  const timestamp = getVersionTimestamp(versionObj);

  if (!timestamp) {
    return `${version} — date unknown`;
  }

  const dt = new Date(timestamp);
  if (Number.isNaN(dt.getTime())) {
    return `${version} — date unknown`;
  }

  const daysOld = daysOldFromDate(timestamp);
  const dateLabel = dt.toLocaleDateString();

  if (daysOld == null) {
    return `${version} — ${dateLabel}`;
  }

  return `${version} — ${dateLabel} (${daysOld} day${daysOld === 1 ? '' : 's'} ago)`;
};

const ReleaseProductsPanel = ({
  releaseType,
  colors,
  selectedRelease,
  releaseLocked,
  productsByReleaseType,
  assetMap,
  patchReleaseItem,
  setAllToLatest,
  setAllToLatestStatus,
  finalizingReleaseType,
  setConfirmDialogReleaseType,
  setConfirmDialogOpen,
  setRecipientDialogReleaseType,
  setRecipientDialogProduct,
  setRecipientDialogOpen,
  onFinalizeTrack,
  onUnfinalizeTrack,
  onUpdateScheduledDate,
  updatingSubReleaseDate,
  finalizeError,
  onClearFinalizeError,
  finalizeDeliveryActions = {},
  onSetFinalizeDeliveryAction,
}) => {
  const key = releaseType === 'Contact' ? 'contact' : 'digital';
  const sub = selectedRelease?.[key];
  const trackEnabled = Boolean(sub?.enabled);
  const items = sub?.items || [];
  const [finalizeDateDialogOpen, setFinalizeDateDialogOpen] = useState(false);


  const defaultFinalizeIso = useMemo(() => addMinutesIso(5), []);
  const initialFinalizeParts = toUtcInputParts(defaultFinalizeIso);

  const [finalizeDate, setFinalizeDate] = useState(initialFinalizeParts.date);
  const [finalizeTime, setFinalizeTime] = useState(initialFinalizeParts.time);

  if (!trackEnabled) {
    return (
      <Stack spacing={2}>
        <ReleaseTrackSummary colors={colors} releaseType={releaseType} track={sub} />
        <Typography variant="caption" sx={{ color: colors.grey[300] }}>
          This release does not include the {releaseType} track.
        </Typography>
      </Stack>
    );
  }

  const versionsMap = new Map(
    (productsByReleaseType?.[releaseType] || []).map((p) => [String(p.dataAssetId), p.versions || []])
  );

  const pendingSelectedCount = items.filter((it) => it?.version && !it?.confirmed).length;
  const totalCount = items.length;
  const allHaveVersion = totalCount > 0 && items.every((it) => Boolean(it?.version));
  const allConfirmed = totalCount > 0 && items.every((it) => Boolean(it?.confirmed));
  const readyToFinalize = allHaveVersion && allConfirmed;

  const trackStatus = sub?.status || 'draft';
  const alreadyFinalized = ['scheduled', 'releasing', 'released'].includes(trackStatus);
  const trackLocked = ['scheduled', 'releasing', 'released', 'canceled'].includes(trackStatus);
  const controlsLocked = releaseLocked && trackLocked;
  const showFinalizeButton = !trackLocked && readyToFinalize && !alreadyFinalized;
  const showUnfinalizeButton = trackStatus === 'scheduled';

  const latestStatus = setAllToLatestStatus?.[releaseType] || {};
  const settingLatest = latestStatus.loading;
  const finalizingThisTrack = finalizingReleaseType === releaseType;
  const hasFinalizeDeliveryActions = Object.keys(finalizeDeliveryActions || {}).length > 0;


  const finalizeIso = buildUtcIso(finalizeDate, finalizeTime);
  const finalizeLocal = formatLocalFromUtc(finalizeIso);
  const finalizeTooSoon = !finalizeIso || new Date(finalizeIso).getTime() <= Date.now();

  const openFinalizeDateDialog = () => {
    const next = toUtcInputParts(addMinutesIso(5));
    setFinalizeDate(next.date);
    setFinalizeTime(next.time);
    setFinalizeDateDialogOpen(true);
  };

  const handleFinalizeClick = () => {
    if (isPastOrTooSoon(sub?.scheduled_release_date_utc)) {
      openFinalizeDateDialog();
      return;
    }

    onFinalizeTrack?.(releaseType);
  };

  const handleFinalizeWithUpdatedDate = async () => {
    if (finalizeTooSoon || updatingSubReleaseDate || finalizingThisTrack) return;

    await onUpdateScheduledDate?.(releaseType, finalizeIso);
    setFinalizeDateDialogOpen(false);
    await onFinalizeTrack?.(releaseType);
  };

  return (
    <Stack spacing={1.5}>
      <ReleaseTrackSummary
        colors={colors}
        releaseType={releaseType}
        track={sub}
        editable
        updating={updatingSubReleaseDate}
        onUpdateScheduledDate={onUpdateScheduledDate}
      />

      <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap" alignItems="center">
        <Box sx={{ flex: 1, minWidth: 320 }}>
          {trackStatus === 'scheduled' ? (
            <Box
              sx={{
                mt: 0.5,
                p: 2,
                borderRadius: 1.5,
                border: `1px solid ${colors.greenAccent[400]}`,
                backgroundColor: colors.greenAccent[900],
                boxShadow: `0 0 0 1px ${colors.greenAccent[700]} inset`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: colors.greenAccent[300],
                  lineHeight: 1.2,
                  mb: 0.75,
                }}
              >
                {releaseType} FINALIZED & SCHEDULED
              </Typography>

              <Typography variant="body2" sx={{ color: colors.grey[100], mb: 1.25 }}>
                Pending deliveries were created for recipient organizations and this sub-release is queued to run on its
                scheduled date.
              </Typography>

              <Box display="flex" gap={1} flexWrap="wrap">
                {sub?.scheduled_release_date_utc ? (
                  <Chip
                    label={`Runs ${new Date(sub.scheduled_release_date_utc).toLocaleString()}`}
                    sx={{
                      height: 30,
                      fontWeight: 700,
                      bgcolor: colors.blueAccent[700],
                      color: colors.grey[100],
                    }}
                  />
                ) : null}

                {sub?.delivery_run_id ? (
                  <Chip
                    label={`Run ID: ${sub.delivery_run_id}`}
                    sx={{
                      height: 30,
                      fontWeight: 700,
                      bgcolor: colors.primary[300],
                      color: colors.grey[100],
                    }}
                  />
                ) : null}

                <Chip
                  label={`${sub?.queued_delivery_count || 0} queued deliveries`}
                  sx={{
                    height: 30,
                    fontWeight: 700,
                    bgcolor: colors.greenAccent[700],
                    color: colors.grey[100],
                  }}
                />
              </Box>
            </Box>
          ) : trackStatus === 'releasing' ? (
            <Box
              sx={{
                mt: 0.5,
                p: 2,
                borderRadius: 1.5,
                border: `1px solid ${colors.blueAccent[400]}`,
                backgroundColor: colors.blueAccent[900],
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, color: colors.blueAccent[200], mb: 0.5 }}>
                {releaseType} CURRENTLY RELEASING
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[100] }}>
                Deliveries for this sub-release are actively being processed.
              </Typography>
            </Box>
          ) : trackStatus === 'released' ? (
            <Box
              sx={{
                mt: 0.5,
                p: 2,
                borderRadius: 1.5,
                border: `1px solid ${colors.greenAccent[400]}`,
                backgroundColor: colors.greenAccent[900],
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, color: colors.greenAccent[300], mb: 0.5 }}>
                {releaseType} COMPLETED
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[100] }}>
                This sub-release has finished delivery.
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
              {readyToFinalize
                ? `${releaseType} is ready for final confirmation and scheduling.`
                : `Confirm all ${releaseType} product versions before final scheduling.`}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            minWidth: 320,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 260 }}>
            {latestStatus.loading ? (
              <Box>
                <Typography variant="caption" sx={{ color: colors.blueAccent[200], fontWeight: 700 }}>
                  Setting all {releaseType} products to latest versions...
                </Typography>
                <LinearProgress
                  sx={{
                    mt: 0.75,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: colors.primary[300],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors.blueAccent[400],
                    },
                  }}
                />
              </Box>
            ) : latestStatus.error ? (
              <Alert
                severity="error"
                sx={{
                  py: 0,
                  backgroundColor: colors.redAccent[900],
                  color: colors.grey[100],
                  border: `1px solid ${colors.redAccent[500]}`,
                  '& .MuiAlert-icon': { color: colors.redAccent[300] },
                }}
              >
                Failed: {latestStatus.error}
              </Alert>
            ) : latestStatus.success ? (
              <Alert
                severity="success"
                sx={{
                  py: 0,
                  backgroundColor: colors.greenAccent[900],
                  color: colors.grey[100],
                  border: `1px solid ${colors.greenAccent[500]}`,
                  '& .MuiAlert-icon': { color: colors.greenAccent[300] },
                }}
              >
                Set latest versions.
              </Alert>
            ) : finalizingThisTrack ? (
              <Box>
                <Typography variant="caption" sx={{ color: colors.greenAccent[200], fontWeight: 700 }}>
                  Finalizing {releaseType}, creating deliveries, and scheduling EventBridge...
                </Typography>
                <LinearProgress
                  sx={{
                    mt: 0.75,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: colors.primary[300],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors.greenAccent[400],
                    },
                  }}
                />
              </Box>
            ) : null}
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap" alignItems="center">
            <Button
              variant="contained"
              disabled={trackLocked || settingLatest || finalizingThisTrack}
              onClick={() => {
                onClearFinalizeError?.();
                setAllToLatest(releaseType);
              }}
              startIcon={settingLatest ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                minWidth: 170,
                fontWeight: 700,
                backgroundColor: colors.blueAccent[500],
                color: colors.grey[100],
                border: `1px solid ${colors.blueAccent[300]}`,
                '&:hover': {
                  backgroundColor: colors.blueAccent[400],
                  borderColor: colors.blueAccent[200],
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.primary[300],
                  color: colors.grey[300],
                  border: `1px solid ${colors.primary[200]}`,
                  opacity: 1,
                },
              }}
            >
              {settingLatest ? 'Setting Latest...' : 'Set All To Latest'}
            </Button>

            <Button
              variant="outlined"
              disabled={trackLocked || pendingSelectedCount === 0 || settingLatest || finalizingThisTrack}
              onClick={() => {
                onClearFinalizeError?.();
                setConfirmDialogReleaseType(releaseType);
                setConfirmDialogOpen(true);
              }}
              sx={{
                minWidth: 170,
                fontWeight: 700,
                borderWidth: 1,
                borderColor: colors.blueAccent[200],
                color: colors.greenAccent[200],
                backgroundColor: 'transparent',
                '&:hover': {
                  borderColor: colors.primary[100],
                  backgroundColor: colors.greenAccent[400],
                  color: colors.grey[100],
                },
                '&.Mui-disabled': {
                  borderColor: colors.primary[200],
                  color: colors.grey[300],
                  backgroundColor: colors.primary[400],
                  opacity: 1,
                },
              }}
            >
              Confirm All Selected
            </Button>

            {showFinalizeButton ? (
              <Button
                variant="contained"
                disabled={settingLatest || finalizingThisTrack}
                onClick={handleFinalizeClick}
                startIcon={finalizingThisTrack ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  backgroundColor: colors.greenAccent[500],
                  color: colors.grey[900],
                  fontWeight: 800,
                  px: 2,
                  minWidth: 230,
                  '&:hover': {
                    backgroundColor: colors.greenAccent[400],
                  },
                  '&.Mui-disabled': {
                    backgroundColor: colors.primary[300],
                    color: colors.grey[300],
                    opacity: 1,
                  },
                }}
              >
                {finalizingThisTrack ? `Scheduling ${releaseType}...` : `Final Confirm / Schedule ${releaseType}`}
              </Button>
            ) : null}

            {showUnfinalizeButton ? (
              <Button
                variant="contained"
                disabled={settingLatest || finalizingThisTrack}
                onClick={() => onUnfinalizeTrack?.(releaseType)}
                sx={{
                  backgroundColor: colors.redAccent[400],
                  color: colors.grey[100],
                  fontWeight: 800,
                  px: 2,
                  '&:hover': {
                    backgroundColor: colors.redAccent[500],
                  },
                }}
              >
                Unfinalize / Unschedule {releaseType}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
      {finalizeError ? (
        <Alert
          severity="warning"
          onClose={onClearFinalizeError}
          sx={{
            backgroundColor: colors.redAccent[900],
            color: colors.grey[100],
            border: `1px solid ${colors.redAccent[500]}`,
            '& .MuiAlert-icon': {
              color: colors.redAccent[300],
            },
            '& .MuiAlert-action': {
              color: colors.grey[100],
            },
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 0.75 }}>
            {finalizeError.message}
          </Typography>

          {finalizeError.count ? (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Blocked planned deliveries: {finalizeError.count}
            </Typography>
          ) : null}

          {Array.isArray(finalizeError.sample) && finalizeError.sample.length ? (
            <Stack spacing={0.75}>
              {finalizeError.sample.slice(0, 10).map((row, idx) => {
                const selectedAction = finalizeDeliveryActions?.[row.delivery_action_key];

                return (
                  <Box
                    key={`${row.organization_id || 'org'}-${row.product || 'product'}-${row.to_version || 'version'}-${row.file_format || 'format'}-${idx}`}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: colors.primary[500],
                      border: `1px solid ${colors.primary[300]}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                      {row.product || 'Unknown Product'} {row.to_version ? `v${row.to_version}` : ''}
                      {row.file_format ? ` — ${row.file_format.toUpperCase()}` : ''}
                    </Typography>

                    <Typography variant="caption" sx={{ display: 'block', color: colors.grey[300] }}>
                      Org: {row.org_name || row.organization_id || 'Unknown org'}
                    </Typography>

                    <Typography variant="caption" sx={{ display: 'block', color: colors.redAccent[200], fontWeight: 700 }}>
                      {(row.blocking_reasons || []).join('; ')}
                    </Typography>

                    {selectedAction ? (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: colors.greenAccent[200],
                          fontWeight: 800,
                          mt: 0.5,
                        }}
                      >
                        Selected fix:{' '}
                        {selectedAction.action === 'skip'
                          ? 'Skip this delivery'
                          : `Replace with ${selectedAction.alternative_key}`}
                      </Typography>
                    ) : null}

                    {row.destination_uri ? (
                      <Typography variant="caption" sx={{ display: 'block', color: colors.grey[300], wordBreak: 'break-all' }}>
                        Destination: {row.destination_uri}
                      </Typography>
                    ) : null}
                    {Array.isArray(row.alternative_deliveries) && row.alternative_deliveries.length ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                        {row.alternative_deliveries.map((alt) => (
                          <Button
                            key={alt.alternative_key}
                            size="small"
                            variant="contained"
                            onClick={() =>
                              onSetFinalizeDeliveryAction?.(row.delivery_action_key, {
                                action: 'replace',
                                alternative_key: alt.alternative_key,
                              })
                            }
                            sx={{
                              fontWeight: 800,
                              backgroundColor: colors.blueAccent[500],
                              color: colors.grey[100],
                              '&:hover': {
                                backgroundColor: colors.blueAccent[400],
                              },
                            }}
                          >
                            {alt.label || `Use ${alt.alternative_key}`}
                          </Button>
                        ))}
                      </Stack>
                    ) : null}

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        onSetFinalizeDeliveryAction?.(row.delivery_action_key, {
                          action: 'skip',
                        })
                      }
                      sx={{
                        mt: 1,
                        fontWeight: 800,
                        borderColor: colors.redAccent[400],
                        color: colors.redAccent[200],
                        '&:hover': {
                          borderColor: colors.redAccent[300],
                          backgroundColor: colors.redAccent[900],
                        },
                      }}
                    >
                      Skip This Delivery
                    </Button>

                  </Box>
                );
              })}

              <Button
                variant="contained"
                disabled={!hasFinalizeDeliveryActions || finalizingThisTrack}
                onClick={() => onFinalizeTrack?.(releaseType)}
                sx={{
                  mt: 1,
                  fontWeight: 800,
                  backgroundColor: colors.greenAccent[500],
                  color: colors.grey[900],
                  '&:hover': {
                    backgroundColor: colors.greenAccent[400],
                  },
                  '&.Mui-disabled': {
                    backgroundColor: colors.primary[300],
                    color: colors.grey[400],
                    opacity: 1,
                  },
                }}
              >
                Retry Finalize With Selected Fixes
              </Button>
              {finalizeError.sample.length > 10 ? (
                <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                  Showing first 10 blocked rows. Check server logs for the full sample.
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Alert>
      ) : null}
      {items.map((it) => {
        const dataAssetId = String(it.data_asset_id);
        const versions = versionsMap.get(dataAssetId) || [];
        const sortedVersions = sortVersionsNewestFirst(versions);
        const selectedMeta = getVersionMetaFromAssetMap(assetMap, releaseType, dataAssetId, it.version);
        const selectedTimestamp = selectedMeta?.timestamp_utc || it?.version_timestamp_utc;
        const selectedDaysOld = daysOldFromDate(selectedTimestamp);
        const availability = getAvailability(selectedMeta);

        return (
          <Box
            key={`${releaseType}:${dataAssetId}`}
            sx={{
              border: `1px solid ${colors.primary[300]}`,
              borderRadius: 1,
              p: 1.5,
              background: colors.primary[500],
            }}
          >
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" alignItems="flex-start">
              <Box sx={{ minWidth: 280, flex: 1 }}>
                <Typography sx={{ fontWeight: 'bold', color: colors.blueAccent[300] }}>
                  {it.product}
                </Typography>

                <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block', mt: 0.5 }}>
                  Selected Version: {it.version || '-'}
                </Typography>

                <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={it.confirmed ? 'Confirmed' : 'Unconfirmed'}
                    sx={{
                      bgcolor: it.confirmed ? colors.greenAccent[700] : colors.redAccent[700],
                      color: colors.grey[100],
                    }}
                  />

                  {it.version ? (
                    <Chip
                      size="small"
                      label={
                        selectedDaysOld == null
                          ? 'Age unknown'
                          : `${selectedDaysOld} day${selectedDaysOld === 1 ? '' : 's'} old`
                      }
                      sx={ageChipSx(selectedDaysOld, colors)}
                    />
                  ) : null}
                </Box>

                {it.version ? (
                  <Box mt={1.25}>
                    <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block', mb: 0.75 }}>
                      Available in:
                    </Typography>

                    <Box display="flex" gap={1} flexWrap="wrap">
                      {availability.hasCsv ? <Chip size="small" label="S3 CSV" /> : null}
                      {availability.hasParquet ? <Chip size="small" label="S3 Parquet" /> : null}
                      {availability.hasSnowflake ? <Chip size="small" label="Snowflake" /> : null}
                    </Box>
                  </Box>
                ) : null}
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <TextField
                  select
                  size="small"
                  label="Version"
                  value={it.version || ''}
                  disabled={trackLocked}
                  onChange={(e) => {
                    onClearFinalizeError?.();

                    const nextVersion = e.target.value;

                    if (!nextVersion) {
                      patchReleaseItem({
                        releaseType,
                        dataAssetId,
                        patch: {
                          version: '',
                          version_timestamp_utc: undefined,
                          confirmed: false,
                        },
                      });
                      return;
                    }

                    const nextMeta = getVersionMetaFromAssetMap(assetMap, releaseType, dataAssetId, nextVersion);

                    patchReleaseItem({
                      releaseType,
                      dataAssetId,
                      patch: {
                        version: nextVersion,
                        version_timestamp_utc: nextMeta?.timestamp_utc || undefined,
                        confirmed: false,
                      },
                    });
                  }}
                  sx={{ minWidth: 320 }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>

                  {sortedVersions.map((v) => (
                    <MenuItem key={v.version} value={v.version}>
                      {formatVersionOptionLabel(v)}
                    </MenuItem>
                  ))}
                </TextField>

                {it.confirmed ? (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={trackLocked}
                    onClick={() =>
                      patchReleaseItem({
                        releaseType,
                        dataAssetId,
                        patch: { confirmed: false },
                      })
                    }
                    sx={{
                      backgroundColor: colors.greenAccent[400],
                      color: colors.grey[900],
                      '&:hover': {
                        backgroundColor: colors.greenAccent[500],
                      },
                      '&.Mui-disabled': {
                        backgroundColor: colors.primary[300],
                        color: colors.grey[500],
                      },
                    }}
                  >
                    Unconfirm
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!it.version || trackLocked}
                    onClick={() =>
                      patchReleaseItem({
                        releaseType,
                        dataAssetId,
                        patch: { confirmed: true },
                      })
                    }
                    sx={{
                      backgroundColor: colors.redAccent[300],
                      color: colors.grey[900],
                      '&:hover': {
                        backgroundColor: colors.redAccent[400],
                      },
                      '&.Mui-disabled': {
                        backgroundColor: colors.primary[300],
                        color: colors.grey[500],
                      },
                    }}
                  >
                    Confirm
                  </Button>
                )}

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setRecipientDialogReleaseType(releaseType);
                    setRecipientDialogProduct(it);
                    setRecipientDialogOpen(true);
                  }}
                  sx={{
                    borderColor: colors.primary[200],
                    color: colors.primary[100],
                    backgroundColor: colors.primary[400],
                    '&:hover': {
                      borderColor: colors.primary[100],
                      backgroundColor: colors.primary[400],
                      color: colors.grey[100],
                    },
                  }}
                >
                  View Recipient Orgs
                </Button>
              </Stack>
            </Box>
          </Box>
        );
      })}
      <Dialog
        open={finalizeDateDialogOpen}
        onClose={() => (!updatingSubReleaseDate && !finalizingThisTrack ? setFinalizeDateDialogOpen(false) : null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Update {releaseType} Schedule Before Finalizing</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert
              severity="warning"
              sx={{
                backgroundColor: colors.redAccent[900],
                color: colors.grey[100],
                border: `1px solid ${colors.redAccent[500]}`,
                '& .MuiAlert-icon': { color: colors.redAccent[300] },
              }}
            >
              The planned release time has already passed. Set a new future time before finalizing.
            </Alert>

            <Button
              variant="contained"
              disabled={updatingSubReleaseDate || finalizingThisTrack}
              onClick={() => {
                const next = toUtcInputParts(addMinutesIso(5));
                setFinalizeDate(next.date);
                setFinalizeTime(next.time);
              }}
              sx={{
                fontWeight: 800,
                backgroundColor: colors.blueAccent[500],
                color: colors.grey[100],
                '&:hover': { backgroundColor: colors.blueAccent[400] },
              }}
            >
              Set To Soonest Allowed: Now + 5 Minutes
            </Button>

            <TextField
              size="small"
              label="Release Day (UTC)"
              type="date"
              value={finalizeDate}
              onChange={(e) => setFinalizeDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              size="small"
              label="Release Time (UTC)"
              type="time"
              value={finalizeTime}
              onChange={(e) => setFinalizeTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
            />

            <Box>
              <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
                UTC: {finalizeIso || 'Invalid date'}
              </Typography>

              {finalizeLocal ? (
                <Typography variant="caption" sx={{ color: colors.greenAccent[200], display: 'block', mt: 0.5, fontWeight: 700 }}>
                  Local ({finalizeLocal.tz}): {finalizeLocal.full}
                </Typography>
              ) : null}

              {finalizeTooSoon ? (
                <Typography variant="caption" sx={{ color: colors.redAccent[300], display: 'block', mt: 0.5, fontWeight: 700 }}>
                  Pick a time after the current time.
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setFinalizeDateDialogOpen(false)}
            disabled={updatingSubReleaseDate || finalizingThisTrack}
            sx={{
              fontWeight: 800,
              color: colors.redAccent[300],
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={finalizeTooSoon || updatingSubReleaseDate || finalizingThisTrack}
            onClick={handleFinalizeWithUpdatedDate}
            startIcon={updatingSubReleaseDate || finalizingThisTrack ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              fontWeight: 800,
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
              '&:hover': { backgroundColor: colors.greenAccent[400] },
              '&.Mui-disabled': {
                backgroundColor: colors.primary[300],
                color: colors.grey[400],
                opacity: 1,
              },
            }}
          >
            {updatingSubReleaseDate ? 'Saving Date...' : finalizingThisTrack ? 'Finalizing...' : 'Save Date & Finalize'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ReleaseProductsPanel;